import type { RealtimeChannel } from "@supabase/supabase-js";

import { supabase } from "@/infrastructure/supabase/client";
import type { ArchitectureWorkspaceStateRow } from "@/infrastructure/supabase/database.types";

// Module-level ref-counted singleton for the realtime channel.
// `useArchitectureWorkspace` is called by multiple hooks on the same page
// (useArchitectureMapPage, useMapWorkspace, useB2bUsersStage). Each consumer's
// useEffect subscribes; without ref-counting, the second subscriber tries to
// add a `postgres_changes` callback to an already-subscribed channel, which
// the Supabase client rejects with:
//   "cannot add `postgres_changes` callbacks for realtime:<topic> after subscribe()"
// Instead, we share one channel across all consumers and tear it down only
// when the last subscriber unmounts.
let sharedChannel: RealtimeChannel | null = null;
const sharedListeners = new Set<() => void>();

function dispatchChange() {
  for (const listener of sharedListeners) listener();
}

function ensureSharedChannel(): RealtimeChannel {
  if (sharedChannel) return sharedChannel;
  sharedChannel = supabase
    .channel("architecture-workspace-state")
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "architecture_workspace_state" },
      dispatchChange
    )
    .subscribe();
  return sharedChannel;
}

export const architectureWorkspaceRepository = {
  async get(): Promise<ArchitectureWorkspaceStateRow> {
    const { data, error } = await supabase
      .from("architecture_workspace_state")
      .select("*")
      .eq("id", 1)
      .maybeSingle();
    if (error) throw error;
    if (!data) {
      throw new Error("architecture_workspace_state row missing — run migrations and seed id=1");
    }
    return data;
  },

  async patch(
    patch: Partial<
      Pick<
        ArchitectureWorkspaceStateRow,
        | "compact_positions"
        | "compact_stage_highlights"
        | "map_positions"
        | "map_stage_highlights"
        | "map_nodes"
        | "map_edges"
        | "b2b_users_nodes"
        | "b2b_users_edges"
        | "b2b_users_positions"
        | "b2b_users_highlights"
      >
    >
  ): Promise<ArchitectureWorkspaceStateRow> {
    const { data, error } = await supabase
      .from("architecture_workspace_state")
      .update(patch)
      .eq("id", 1)
      .select("*")
      .single();
    if (error) throw error;
    return data;
  },

  subscribeToChanges(onChange: () => void) {
    ensureSharedChannel();
    sharedListeners.add(onChange);
    return () => {
      sharedListeners.delete(onChange);
      if (sharedListeners.size === 0 && sharedChannel) {
        const channel = sharedChannel;
        sharedChannel = null;
        void supabase.removeChannel(channel);
      }
    };
  },
};
